import React from "react";
import { Gavel, FileText, Search, Users2 } from "lucide-react";
import CTABand from "../components/CTABand";
import { useSEO } from "../lib/seo";

export default function LitigationSupport() {
  useSEO({
    title:
      "Healthcare Litigation Support · Long-Term Care Defense · Missouri",
    description:
      "Operational chronologies, record reconstruction, expert collaboration, and pre-deposition briefings for defense counsel handling Missouri long-term care and skilled nursing litigation.",
    path: "/litigation-support",
  });
  return (
    <div data-testid="litigation-page" className="bg-ivory">
      <section className="section-pad pt-32 bg-paper">
        <div className="container-luxe max-w-5xl">
          <div className="overline mb-5">Practice Area · Healthcare Litigation Support</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-navy leading-[0.95] mb-6">
            Operational fluency for the<br />
            <span className="italic text-teal">defense team.</span>
          </h1>
          <p className="font-sans text-lg text-navy/75 max-w-3xl leading-relaxed">
            Defense counsel needs operational context. They need to understand
            what the standard of care actually looks like inside long-term
            care, how documentation flows, and where the facility's record
            tells the right story. AthenaCrest works alongside counsel to
            translate operations into a defensible narrative.
          </p>
          <p className="mt-6 font-sans text-sm text-navy/55 italic">
            AthenaCrest Defense provides consulting services. Engagements do not
            constitute legal advice or establish an attorney-client relationship.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-luxe">
          <div className="overline mb-5">How We Support Counsel</div>
          <h2 className="font-serif text-4xl sm:text-5xl text-navy leading-tight mb-16 max-w-3xl">
            The operational story counsel can defend in writing, and in court.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-champagne-200 border border-champagne-200">
            {[
              {
                icon: FileText,
                title: "Operational Chronologies",
                body: "Detailed reconstructions of resident care, IDT decisions, and facility response. Built from the record and presented in language a jury can follow.",
              },
              {
                icon: Search,
                title: "Record Reconstruction",
                body: "Identification of corroborating documentation across nursing, therapy, social services, dietary, and ancillary services that strengthens the defense narrative.",
              },
              {
                icon: Users2,
                title: "Expert Witness Collaboration",
                body: "Operational briefings for retained experts so their opinions align with how the facility actually functions day to day.",
              },
              {
                icon: Gavel,
                title: "Pre Deposition Operational Briefings",
                body: "Prep sessions for facility witnesses covering operational vocabulary, IDT roles, and how routine practices show up in the record.",
              },
            ].map((d) => (
              <div key={d.title} className="bg-ivory p-10">
                <div className="w-12 h-12 border border-gold flex items-center justify-center mb-5">
                  <d.icon className="h-5 w-5 text-gold" />
                </div>
                <h3 className="font-serif text-2xl text-navy mb-3">{d.title}</h3>
                <p className="font-sans text-sm text-navy/70 leading-relaxed">
                  {d.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        testId="litigation-cta-band"
        eyebrow="Defense Engagements"
        headline="The earlier AthenaCrest enters the case, the more usable the record becomes."
        sub="Confidential engagement intake available. Calls returned same day for active matters."
      />
    </div>
  );
}
